import React from 'react';
import { createPortal } from 'react-dom';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingArrow,
  arrow,
  useClick,
} from '@floating-ui/react';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import { ENABLE_OPTION_FLOW_READ_MODE } from 'utils/shared/trades/optionFlowRead';
import { mapCondensedSectors } from 'utils/shared/trades/sectors';
import { FaSackDollar } from 'react-icons/fa6';
import { LuScale3D } from 'react-icons/lu';
import { BsGraphUpArrow, BsGraphDownArrow } from 'react-icons/bs';
import { TbZoomMoney } from 'react-icons/tb';
import { calculateSingleLegMetrics } from 'utils/shared/quantlib/singleLegCalculator';
import styled from 'styled-components';
import { rgba } from 'styles/utils/color';
import { CallPutBackground, CallPutText } from 'components/Table';
import { HeatScore } from 'components/Score';
import OptionFlowTradeSide from 'components/OptionFlowTradeSide/OptionFlowTradeSide';
import { TbCalendarDollar } from 'react-icons/tb';
import TooltipComponent from 'components/Tooltip';
import { getEarningsTooltip } from 'utils/shared/earnings';
import { Trade } from 'utils/shared/trades/types';
import {
  getOptionFlowRowSentiment,
  getOptionFlowSideCode,
  getOptionFlowStrategyPosition,
  OptionFlowStrategyPosition,
} from 'utils/shared/trades/getOptionFlowRowSentiment';
import { TickerAggregatedDetails } from 'components/OptionAggregatedTable/props';
import Modal from 'components/Modal/HeadlessUiModal';
import { useDarkPoolData } from './MoreFeatureModal/hooks/useDarkPoolData';
import OptionFlowTradeRead from 'components/OptionFlowTradeSide/OptionFlowTradeRead';

interface DarkPoolDisplayData {
  premium?: number;
  volume?: number;
}

interface OptionsPopoverProps {
  children: React.ReactNode;
  tradesByTicker?: TickerAggregatedDetails[];
  darkPoolByTicker?: DarkPoolDisplayData;
  fullTradeData?: Trade;
  tickerHasEarnings?: boolean;
  earningsData?: any;
  daysToEarnings?: number | null;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  anchorElement?: HTMLElement | null;
}

const getFullSectorName = (sector?: string): string => {
  if (!sector) return 'N/A';
  return mapCondensedSectors(sector);
};

const MutedText = styled.div`
  color: ${({ theme }) => theme.color.textSecondary};
  font-size: 85%;
`;

const PopoverWrapper = styled.div<{
  isUnusual: boolean;
  isGoldenSweep: boolean;
}>`
  position: relative;
  width: 100%;
  min-width: 400px;
  max-width: 700px;
  border-radius: 0.375rem;
  background-color: #1c1e27;
  box-shadow: ${({ theme }) => theme.shadow.medium};
  transition: border 0.2s ease;

  @media (max-width: 768px) {
    max-width: calc(100vw - 2rem);
    width: calc(100vw - 2rem);
  }
`;

const PopoverBackground = styled.div<{
  isUnusual: boolean;
  isGoldenSweep: boolean;
}>`
  background: ${({ isUnusual, isGoldenSweep, theme }) => {
    if (isGoldenSweep) {
      return `linear-gradient(
            -90deg,
            rgba(0, 0, 0, 0) 2%,
            ${rgba(theme.color.mixedSentiment, 0.19)} 32%,
            ${rgba(theme.color.mixedSentiment, 0.14)} 74%,
            rgba(0, 0, 0, 0) 98%);`;
    }

    if (isUnusual) {
      return `linear-gradient(
        -90deg,
        rgba(0, 0, 0, 0) 2%,
        ${rgba(theme.color.unusualTrade, 0.19)} 32%,
        ${rgba(theme.color.unusualTrade, 0.14)} 74%,
        rgba(0, 0, 0, 0) 98%
      )`;
    }

    return 'transparent';
  }};
  border-radius: 0.375rem;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const OptionsPopoverContent: React.FC<{
  tradesByTicker?: TickerAggregatedDetails[];
  darkPoolByTicker?: DarkPoolDisplayData;
  fullTradeData?: Trade;
  tickerHasEarnings?: boolean;
  earningsData?: any;
  daysToEarnings?: number | null;
  singleLegMetrics: any;
  isStrategyNeutral?: boolean;
  imageError: boolean;
  setImageError: (error: boolean) => void;
  sectorName: string;
  isMobile?: boolean;
}> = ({
  tradesByTicker,
  darkPoolByTicker,
  fullTradeData,
  tickerHasEarnings,
  earningsData,
  daysToEarnings,
  singleLegMetrics,
  isStrategyNeutral = false,
  imageError,
  setImageError,
  sectorName,
  isMobile = false,
}) => {
  const iconColor = tickerHasEarnings ? 'text-graph-purple' : 'text-gray-400';
  const sentiment = getOptionFlowRowSentiment(fullTradeData);
  const orderClassification = fullTradeData?.isGoldenSweep
    ? 'Golden Sweep'
    : fullTradeData?.isTopPosition
    ? 'Top Position'
    : fullTradeData?.isUnusual
    ? 'Unusual'
    : (fullTradeData?.heatScore || 0) > 15
    ? 'High Heat'
    : getOptionFlowSideCode(fullTradeData) === 'AA'
    ? 'Aggressive Entry'
    : 'Standard Order';

  return (
    <div className="max-h-[80vh] overflow-y-auto overflow-x-hidden">
      {/* SECTION 1: OVERVIEW - Aggregated Data */}
      <div className="mb-3 border-b border-gray-700 pb-3">
        <div className={`mb-3 flex items-start gap-2 ${isMobile ? 'hidden' : ''}`}>
          {!imageError ? (
            <img
              src={`https://images.financialmodelingprep.com/symbol/${
                fullTradeData?.ticker || ''
              }.png`}
              alt={`${fullTradeData?.ticker || ''} logo`}
              className="h-8 w-8 flex-shrink-0 rounded"
              onError={() => {
                setImageError(true);
              }}
            />
          ) : (
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-palette-black-5 text-xs font-bold text-white">
              {fullTradeData?.ticker || '?'}
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            <div className="flex items-start gap-1 text-sm font-bold capitalize">
              <div>
                {fullTradeData?.tickerDetails?.name || 'OVERVIEW'}
                {fullTradeData?.tickerDetails?.sector && (
                  <div className="text-xs font-normal capitalize text-gray-400">{sectorName}</div>
                )}
              </div>
              <div
                className={`rounded-[4px] px-0.5 py-0.5 transition-all duration-300 sm:px-1 sm:py-1 ${iconColor} shadow-lg`}
              >
                <TooltipComponent
                  title={getEarningsTooltip(earningsData, daysToEarnings ?? null)}
                  position="bottom"
                >
                  <TbCalendarDollar aria-hidden="true" className="h-4 w-4" />
                </TooltipComponent>
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-full gap-6 whitespace-nowrap">
          <div className="flex w-1/2 flex-col space-y-1.5 text-xs leading-none">
            {/* Call/Put Premium Bar */}
            <div className="mb-1">
              <div className="mb-1">Call/Put Premium</div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-800">
                <div className="flex h-full w-full">
                  <div
                    className="bg-graph-green"
                    style={{
                      width: `${
                        ((tradesByTicker?.[0]?.callPremium ?? 0) /
                          ((tradesByTicker?.[0]?.callPremium ?? 0) +
                            (tradesByTicker?.[0]?.putPremium ?? 0))) *
                          100 || 0
                      }%`,
                    }}
                  />
                  <div
                    className="bg-graph-red"
                    style={{
                      width: `${
                        ((tradesByTicker?.[0]?.putPremium ?? 0) /
                          ((tradesByTicker?.[0]?.callPremium ?? 0) +
                            (tradesByTicker?.[0]?.putPremium ?? 0))) *
                          100 || 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Trades</div>
              <div>{tradesByTicker?.[0]?.tradeCount || 0}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>Avg Exp Days</div>
              <div>{tradesByTicker?.[0]?.expiry ?? 0}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Avg Price</div>
              <div>${(tradesByTicker?.[0]?.price ?? 0).toFixed(2)}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Avg OTM</div>
              <div>{Math.round(tradesByTicker?.[0]?.otm ?? 0)}%</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Dark Pool Premium</div>
              <div>${abbreviateNumber(darkPoolByTicker?.premium ?? 0)}</div>
            </div>
          </div>
          <div className="flex w-1/2 flex-col space-y-1.5 text-xs leading-none">
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Total Contracts</div>
              <div>{abbreviateNumber(tradesByTicker?.[0]?.contracts ?? 0)}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Total Premium</div>
              <div>${abbreviateNumber(tradesByTicker?.[0]?.premium ?? 0)}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap text-accent-blue">Unusual Contracts</div>
              <div>{abbreviateNumber(tradesByTicker?.[0]?.unusualContracts ?? 0)}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap text-accent-blue">Unusual Premium</div>
              <div>${abbreviateNumber(tradesByTicker?.[0]?.unusualPremium ?? 0)}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap text-[#B64EC7]">Sweep Contracts</div>
              <div>{abbreviateNumber(tradesByTicker?.[0]?.sweepContracts ?? 0)}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap text-[#B64EC7]">Sweep Premium</div>
              <div>{abbreviateNumber(tradesByTicker?.[0]?.sweepPremium ?? 0)}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Dark Pool Volume</div>
              <div>{abbreviateNumber(darkPoolByTicker?.volume ?? 0)}</div>
            </div>
          </div>
        </div>
      </div>
      {/* SECTION 2: FLOW - Current Trade Details */}
      <div className="mb-3 border-b border-gray-700 pb-3">
        <div className="grid w-full grid-cols-1 gap-x-6 gap-y-2 text-xs leading-none sm:grid-cols-2">
          <div className="flex min-w-0 flex-col">
            <div className="mb-2">
              <MutedText>
                {fullTradeData?.date} {fullTradeData?.time} {fullTradeData?.timeSubscript}
              </MutedText>
            </div>
            <div className="flex items-center">
              <CallPutBackground type={fullTradeData?.cp || ''}>
                {fullTradeData?.ticker}
              </CallPutBackground>
              <CallPutText className="ml-2 font-bold" type={sentiment}>
                ${abbreviateNumber(fullTradeData?.premium || 0)}
              </CallPutText>
            </div>
            <div className="mt-1.5 flex flex-col gap-1.5">
              <div className="flex items-center leading-none">
                <span className="text-white">
                  {fullTradeData?.size?.toLocaleString() || '0'} @ $
                  {fullTradeData?.price?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 leading-none">
                <CallPutText className="inline font-semibold" type={fullTradeData?.cp || ''}>
                  {fullTradeData?.cp || 'Call'}
                </CallPutText>
                <OptionFlowTradeSide trade={fullTradeData} />
                <span className="text-white">{fullTradeData?.orderType || '-'}</span>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-white">
                <span>{orderClassification}</span>
                {ENABLE_OPTION_FLOW_READ_MODE && (
                  <OptionFlowTradeRead trade={fullTradeData} variant="popover" />
                )}
              </div>
              <div className="flex items-center">
                <div className="mr-4 shrink-0 text-xs">Heat Score</div>
                <div className="w-full pt-0.5" style={{ maxWidth: '64px' }}>
                  <HeatScore scoreWidth={fullTradeData?.scoreWidth ?? 0} />
                </div>
              </div>
            </div>
          </div>
          <div className="flex min-w-0 flex-col justify-between space-y-1">
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Expiration</div>
              <div>{fullTradeData?.expiryString || 'N/A'}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Strike</div>
              <div>${fullTradeData?.strike || '0'}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Spot</div>
              <div>${fullTradeData?.spot?.toFixed(2) || '0.00'}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">OTM</div>
              <div>{Math.round(fullTradeData?.otmPercent || 0)}%</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Open Interest</div>
              <div>{fullTradeData?.openInterest?.toLocaleString() || '0'}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Size/OI</div>
              <div>
                {fullTradeData?.openInterest && fullTradeData?.openInterest > 0
                  ? ((fullTradeData?.size || 0) / fullTradeData.openInterest).toFixed(2)
                  : 'N/A'}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="whitespace-nowrap">Implied Vol</div>
              <div>{Math.round((fullTradeData?.impliedVol || 0) * 100)}%</div>
            </div>
          </div>
        </div>
      </div>
      {/* SECTION 3: PAYOFF & GREEKS */}
      <div className="mb-2 whitespace-nowrap">
        {isStrategyNeutral && (
          <div className="mb-2 rounded border border-gray-700 bg-palette-black-4 px-2 py-1.5 text-[0.65rem] leading-tight text-gray-300">
            Midpoint print: no long/short payoff inferred.
          </div>
        )}
        <div className="flex w-full gap-6">
          <div className="flex w-1/2 flex-col space-y-1.5 text-xs leading-none">
            <div className="flex items-center justify-between gap-3">
              <div className="flex gap-1">
                <FaSackDollar className="h-2.5 w-2.5 text-blue-400" />
                Prem Paid
              </div>
              <div> ${abbreviateNumber(fullTradeData?.premium || 0)}</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="flex gap-1">
                <BsGraphUpArrow className="h-2.5 w-2.5 text-green-500" />
                Max Profit
              </div>
              <div>
                {singleLegMetrics &&
                typeof singleLegMetrics.maxProfit === 'number' &&
                isFinite(singleLegMetrics.maxProfit)
                  ? `$${new Intl.NumberFormat('en-US').format(
                      parseInt(singleLegMetrics.maxProfit.toFixed(0), 10),
                    )}`
                  : singleLegMetrics?.maxProfit || 'N/A'}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="flex gap-1">
                <BsGraphDownArrow className="h-2.5 w-2.5 text-red-500" />
                Max Loss
              </div>
              <div>
                {typeof singleLegMetrics?.maxLoss === 'number' && isFinite(singleLegMetrics.maxLoss)
                  ? `$${new Intl.NumberFormat('en-US').format(
                      Math.abs(parseInt(singleLegMetrics.maxLoss.toFixed(0), 10)),
                    )}`
                  : singleLegMetrics?.maxLoss === 'Infinite'
                  ? 'Infinite'
                  : 'N/A'}
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex gap-1">
                <LuScale3D className="h-2.5 w-2.5 text-cyan-400" />
                Breakeven
              </div>
              <div>
                {singleLegMetrics?.breakEvenPrice
                  ? singleLegMetrics.breakEvenPrice.toFixed(2)
                  : 'N/A'}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="flex gap-1">
                <TbZoomMoney className="h-2.5 w-2.5 text-yellow-400" />
                Prob. Profit
              </div>
              <div>
                {typeof singleLegMetrics?.probabilityOfProfit === 'number' &&
                isFinite(singleLegMetrics.probabilityOfProfit)
                  ? (singleLegMetrics.probabilityOfProfit * 100).toFixed(2) + '%'
                  : 'N/A'}
              </div>
            </div>
          </div>
          <div className="flex w-1/2 flex-col space-y-1.5 text-xs leading-none">
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="text-blue-400">Δ</span>
                Delta
              </span>
              <span>{fullTradeData?.delta ? fullTradeData.delta.toFixed(2) : ''}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="text-green-400">Γ</span>
                Gamma
              </span>
              <span>{fullTradeData?.gamma ? fullTradeData.gamma.toFixed(2) : ''}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="text-purple-400">Θ</span>
                Theta
              </span>
              <span>{fullTradeData?.theta ? fullTradeData.theta.toFixed(2) : ''}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="text-yellow-400">ν</span>
                Vega
              </span>
              <span>{fullTradeData?.vega ? fullTradeData.vega.toFixed(2) : ''}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="text-red-400">ρ</span>
                Rho
              </span>
              <span>{fullTradeData?.rho ? fullTradeData.rho.toFixed(2) : ''}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OptionsPopover: React.FC<OptionsPopoverProps> = ({
  children,
  tradesByTicker,
  darkPoolByTicker,
  fullTradeData,
  tickerHasEarnings,
  earningsData,
  daysToEarnings,
  isOpen: externalIsOpen,
  onOpenChange: externalOnOpenChange,
  anchorElement,
}) => {
  const strategyPosition = React.useMemo(
    () => getOptionFlowStrategyPosition(fullTradeData),
    [fullTradeData?.tradeDirection, fullTradeData?.tradeSide],
  );
  const isStrategyNeutral = strategyPosition === OptionFlowStrategyPosition.Neutral;
  const singleLegMetrics = React.useMemo(() => {
    if (!fullTradeData) return null;

    // Same spot fallback as the 3-dot modal (usePayoffCalculation): rows missing a
    // live spot at ingest still carry priceExternalApi.
    const spot = fullTradeData.spot || fullTradeData.priceExternalApi;

    try {
      if (
        spot &&
        fullTradeData.strike &&
        fullTradeData.price &&
        fullTradeData.cp &&
        fullTradeData.expiryString
      ) {
        if (strategyPosition === OptionFlowStrategyPosition.Neutral) {
          return null;
        }

        return calculateSingleLegMetrics({
          spot,
          strike: fullTradeData.strike,
          premium: fullTradeData.price,
          type:
            fullTradeData.cp?.toLowerCase() === 'c' || fullTradeData.cp?.toLowerCase() === 'call'
              ? 'call'
              : 'put',
          numberOfContracts: strategyPosition === OptionFlowStrategyPosition.Long ? 1 : -1,
          volatility: fullTradeData.impliedVol || 0.3,
          expirationDateString: fullTradeData.expiryString,
        });
      }
    } catch (error) {
      // Silently fail for single leg metrics calculation
    }

    return null;
  }, [
    fullTradeData?.spot,
    fullTradeData?.priceExternalApi,
    fullTradeData?.strike,
    fullTradeData?.price,
    fullTradeData?.cp,
    fullTradeData?.expiryString,
    fullTradeData?.tradeDirection,
    fullTradeData?.tradeSide,
    fullTradeData?.impliedVol,
    strategyPosition,
  ]);

  const [internalIsOpen, setInternalIsOpen] = React.useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = externalOnOpenChange || setInternalIsOpen;

  // Fetch dark pool data for this specific ticker when popover is open
  const { darkPoolByTicker: fetchedDarkPoolData } = useDarkPoolData(
    fullTradeData?.ticker ?? undefined,
    isOpen,
  );
  const effectiveDarkPoolData = fetchedDarkPoolData?.premium
    ? fetchedDarkPoolData
    : darkPoolByTicker;

  const [imageError, setImageError] = React.useState(false);

  const arrowRef = React.useRef(null);

  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' && window.innerWidth < 768,
  );

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: isMobile ? 'bottom' : 'right',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip(),
      flip({
        fallbackPlacements: isMobile ? ['top', 'bottom'] : ['left', 'right', 'top', 'bottom'],
      }),
      shift({ padding: isMobile ? 5 : 10 }),
      arrow({ element: arrowRef }),
    ],
    strategy: 'fixed',
  });

  const click = useClick(context);
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, focus, dismiss, role]);

  const sectorName = React.useMemo(
    () => getFullSectorName(fullTradeData?.tickerDetails?.sector ?? undefined),
    [fullTradeData?.tickerDetails?.sector],
  );

  React.useEffect(() => {
    if (anchorElement && isOpen) {
      refs.setReference(anchorElement);
    }
  }, [anchorElement, isOpen, refs]);

  React.useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleScroll = () => {
      setIsOpen(false);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, setIsOpen, isMobile]);

  // Mobile: Render as Modal
  if (isMobile) {
    return (
      <>
        {!anchorElement && (
          <div onClick={() => setIsOpen(true)} className="inline-block cursor-pointer">
            {children}
          </div>
        )}

        <Modal
          isOpen={isOpen}
          handleClose={() => setIsOpen(false)}
          classNameMaxWidth="max-w-2xl"
          classNameHeight="h-auto"
          positionBottomDesktop={false}
          centerOnMobile={false}
          className="border border-brand-primary"
        >
          <div className="flex max-h-[90vh] flex-col bg-palette-black-3 text-white">
            {/* Header */}
            <div className="flex-shrink-0 border-b border-palette-border px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {!imageError ? (
                    <img
                      src={`https://images.financialmodelingprep.com/symbol/${
                        fullTradeData?.ticker || ''
                      }.png`}
                      alt={`${fullTradeData?.ticker || ''} logo`}
                      className="h-8 w-8 flex-shrink-0 rounded"
                      onError={() => {
                        setImageError(true);
                      }}
                    />
                  ) : (
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-palette-black-5 text-xs font-bold text-white">
                      {fullTradeData?.ticker || '?'}
                    </div>
                  )}
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-start gap-1 text-sm font-bold capitalize">
                      <div>
                        {fullTradeData?.tickerDetails?.name || 'OVERVIEW'}
                        {fullTradeData?.tickerDetails?.sector && (
                          <div className="text-xs font-normal capitalize text-gray-400">
                            {sectorName}
                          </div>
                        )}
                      </div>
                      <div
                        className={`rounded-[4px] px-0.5 py-0.5 transition-all duration-300 sm:px-1 sm:py-1 ${
                          tickerHasEarnings ? 'text-graph-purple' : 'text-gray-400'
                        } shadow-lg`}
                      >
                        <TooltipComponent
                          title={getEarningsTooltip(earningsData, daysToEarnings ?? null)}
                          position="bottom"
                        >
                          <TbCalendarDollar aria-hidden="true" className="h-4 w-4" />
                        </TooltipComponent>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 transition-colors hover:text-white"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto sm:px-4 sm:py-4">
              <PopoverBackground
                isGoldenSweep={fullTradeData?.isGoldenSweep || false}
                isUnusual={fullTradeData?.isUnusual || false}
              >
                <OptionsPopoverContent
                  tradesByTicker={tradesByTicker}
                  darkPoolByTicker={effectiveDarkPoolData}
                  fullTradeData={fullTradeData}
                  tickerHasEarnings={tickerHasEarnings}
                  earningsData={earningsData}
                  daysToEarnings={daysToEarnings}
                  singleLegMetrics={singleLegMetrics}
                  isStrategyNeutral={isStrategyNeutral}
                  imageError={imageError}
                  setImageError={setImageError}
                  sectorName={sectorName}
                  isMobile={true}
                />
              </PopoverBackground>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  return (
    <>
      {!anchorElement && (
        <div ref={refs.setReference} {...getReferenceProps()} className="inline-block">
          {children}
        </div>
      )}

      {isOpen &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[9999] rounded-md border border-brand-primary"
          >
            <PopoverWrapper
              isGoldenSweep={fullTradeData?.isGoldenSweep || false}
              isUnusual={fullTradeData?.isUnusual || false}
            >
              <FloatingArrow
                ref={arrowRef}
                context={context}
                fill="#303fe1"
                stroke="rgb(var(--color-brand-primary))"
                strokeWidth={1}
              />
              <PopoverBackground
                isGoldenSweep={fullTradeData?.isGoldenSweep || false}
                isUnusual={fullTradeData?.isUnusual || false}
              >
                <OptionsPopoverContent
                  tradesByTicker={tradesByTicker}
                  darkPoolByTicker={effectiveDarkPoolData}
                  fullTradeData={fullTradeData}
                  tickerHasEarnings={tickerHasEarnings}
                  earningsData={earningsData}
                  daysToEarnings={daysToEarnings}
                  singleLegMetrics={singleLegMetrics}
                  isStrategyNeutral={isStrategyNeutral}
                  imageError={imageError}
                  setImageError={setImageError}
                  sectorName={sectorName}
                />
              </PopoverBackground>
            </PopoverWrapper>
          </div>,
          document.body,
        )}
    </>
  );
};

export default OptionsPopover;
